import React, { useState } from "react";
import {
  Divider,
  Typography,
  LinearProgress,
  makeStyles,
  Box,
  Grid,
} from "@material-ui/core";
import {
  RemoveCircleOutline,
  AddCircleOutline,
  FlagOutlined,
  WhereToVoteOutlined,
} from "@material-ui/icons";
import { ZoomIn, ZoomOut } from "@material-ui/icons";
import { ToggleButtonGroup, ToggleButton, Alert } from "@material-ui/lab";
import ProgressChart from "./ProgressChart";
import ProgressReport from "./ProgressReport";
import * as dayjs from "dayjs";
import { COLORS, SEASONS_ORDERED } from "../../types";
import ac from "../../img/ac.svg";

const useStyles = makeStyles({
  seasonRoot: {
    height: 10,
    borderRadius: 5,
  },
  seasonBar: {
    height: 10,
  },
  tpRoot: {
    height: 10,
    borderRadius: 5,
    backgroundColor: "#5e410c",
  },
  tpBar: {
    height: 10,
    backgroundColor: COLORS.SEASON_QUESTS,
  },
  ac: {
    height: "1.25em",
    verticalAlign: "middle",
  },
});

// this season's treasure pack requirement
const currSeasonReqTreasurePacks = 45;

const tpStatusReport = ({
  missingPacks,
  statusDaysRemaining: value,
  classes,
}) => {
  if (missingPacks === 0) {
    return (
      <Alert severity="success" variant="outlined">
        All {currSeasonReqTreasurePacks} Treasure Packs collected
      </Alert>
    );
  }

  if (value > 5) {
    return (
      <Alert severity="success" variant="outlined">
        You can miss up to{" "}
        <strong>
          {value} day{value === 1 ? "" : "s"}
        </strong>{" "}
        before having to catch up
      </Alert>
    );
  } else if (value > 0) {
    return (
      <Alert severity="warning" variant="outlined">
        Careful, you can only miss{" "}
        <strong>
          {value} day{value === 1 ? "" : "s"}
        </strong>{" "}
        before having to catch up
      </Alert>
    );
  } else if (value === 0) {
    return (
      <Alert severity="error" variant="outlined">
        Careful, you can't miss any more days without having to catch up
      </Alert>
    );
  } else if (value < 0) {
    return (
      <Alert severity="error" variant="outlined">
        You are{" "}
        <strong>
          {Math.abs(value)} day{Math.abs(value) === 1 ? "" : "s"} behind
        </strong>{" "}
        ({Math.abs(value) * 25}
        <img src={ac} className={classes.ac} alt="AC" /> spend required to catch
        up)
      </Alert>
    );
  }
};

export default ({ userData, totalPacks, heirloomRecord }) => {
  const classes = useStyles();
  const [relativeView, setRelativeView] = useState(true);
  const { seasonalTreasurePacks } = userData;

  const handleRelativity = (event, newRel) => {
    if (newRel !== null) {
      setRelativeView(newRel);

      window.gtag("event", "click", {
        event_category: "Chart Zoom Control",
        event_action: newRel ? "Zoom Out" : "Zoom In",
      });
    }
  };

  // find the current season based on end dates
  const findCurrentSeason = () => {
    let result = undefined;
    for (let i = SEASONS_ORDERED.length - 1; i > 0; i--) {
      const seasonEnded = dayjs(SEASONS_ORDERED[i]?.END_DATE).isBefore(
        dayjs(),
        "day"
      );

      if (seasonEnded) {
        // if positive result, return the first entry
        if (i === SEASONS_ORDERED.length - 1) {
          result = i;
          break;
        }

        // found our target that's not at the top of the stack
        result = i + 1;
        break;
      }
    }

    return result;
  };

  const currentSeason = findCurrentSeason();
  const seasonEnd = dayjs(SEASONS_ORDERED[currentSeason].END_DATE);
  const daysLeftInSeason = seasonEnd.diff(dayjs(), "days");
  const currentSeasonTreasPacks = seasonalTreasurePacks[currentSeason] || 0;
  const missingPacks = currSeasonReqTreasurePacks - currentSeasonTreasPacks;
  const statusDaysRemaining = daysLeftInSeason - missingPacks;
  // statusDaysRemaining
  // - positive integer: more days avialable than missing treasure packs (GREEN)
  // - 0: equal number of days / missing treasure packs
  // - negative integer: more treasure packs missing than days available,
  //   the absolute value is the number of "skip ahead" days needed to collect all {currSeasonReqTreasurePacks}

  return (
    <>
      <Typography variant="h5">Heirloom Progress</Typography>
      <ProgressChart
        userData={userData}
        relativeView={relativeView}
        totalPacks={totalPacks}
        heirloomRecord={heirloomRecord}
      />
      <div style={{ textAlign: "right" }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={relativeView}
          onChange={handleRelativity}
        >
          <ToggleButton key={1} value={true}>
            <ZoomOut />
          </ToggleButton>
          <ToggleButton key={2} value={false}>
            <ZoomIn />
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      <Divider
        className="width-constrained"
        style={{ marginTop: 40, marginBottom: 48 }}
      />

      <Grid
        container
        spacing={3}
        justify="center"
        alignItems="stretch"
        className="width-constrained"
        style={{ marginTop: "2em", marginBottom: "2em" }}
      >
        <Grid item xs={12} sm={3} md={3} lg={3} style={{ textAlign: "center" }}>
          <div
            style={{ fontSize: "3em", fontWeight: "bold", lineHeight: "1em" }}
          >
            <WhereToVoteOutlined style={{ fontSize: "0.75em", opacity: 0.6 }} />
            {totalPacks}
          </div>
          <small
            style={{
              display: "inline-block",
              lineHeight: "1.2em",
              opacity: 0.8,
            }}
          >
            Current Apex Packs
          </small>
        </Grid>
        {heirloomRecord !== null && (
          <>
            <Grid
              item
              xs={12}
              sm={3}
              md={3}
              lg={3}
              style={{ textAlign: "center" }}
            >
              <div
                style={{
                  fontSize: "3em",
                  fontWeight: "bold",
                  lineHeight: "1em",
                }}
              >
                <RemoveCircleOutline
                  style={{ fontSize: "0.75em", opacity: 0.6 }}
                />
                <span
                  style={{
                    transition: "color 200ms ease-in-out",
                  }}
                >
                  {heirloomRecord}
                </span>
              </div>
              <small
                style={{
                  display: "inline-block",
                  lineHeight: "1.2em",
                  opacity: 0.8,
                }}
              >
                Apex Packs applied to previous&nbsp;Heirlooms
              </small>
            </Grid>
            <Grid
              item
              xs={12}
              sm={3}
              md={3}
              lg={3}
              style={{ textAlign: "center" }}
            >
              <div
                style={{
                  fontSize: "3em",
                  fontWeight: "bold",
                  lineHeight: "1em",
                }}
              >
                <AddCircleOutline
                  style={{ fontSize: "0.75em", opacity: 0.6 }}
                />
                {heirloomRecord + 500 - totalPacks}
              </div>
              <small
                style={{
                  display: "inline-block",
                  lineHeight: "1.2em",
                  opacity: 0.8,
                }}
              >
                Additional Apex Packs&nbsp;Needed
              </small>
            </Grid>
          </>
        )}
        <Grid item xs={12} sm={3} md={3} lg={3} style={{ textAlign: "center" }}>
          <div
            style={{ fontSize: "3em", fontWeight: "bold", lineHeight: "1em" }}
          >
            <FlagOutlined style={{ fontSize: "0.75em", opacity: 0.6 }} />
            <span
              style={{
                transition: "color 200ms ease-in-out",
              }}
            >
              {heirloomRecord + 500}
            </span>
          </div>
          <small
            style={{
              display: "inline-block",
              lineHeight: "1.2em",
              opacity: 0.8,
            }}
          >
            Next Heirloom Bad Luck&nbsp;Protection
          </small>
        </Grid>
      </Grid>

      <Divider
        className="width-constrained"
        style={{ marginTop: 40, marginBottom: 48 }}
      />

      <Typography variant="h5" className="width-constrained">
        Heirloom Progress Report
      </Typography>
      <ProgressReport
        userData={userData}
        totalPacks={totalPacks}
        heirloomRecord={heirloomRecord}
      />

      <Divider
        className="width-constrained"
        style={{ marginTop: 40, marginBottom: 48 }}
      />

      <div className="width-constrained">
        <Typography variant="h5">Treasure Packs Countdown</Typography>
        <Box display="flex" alignItems="center">
          <Box width="100%" mr={1}>
            <LinearProgress
              variant="determinate"
              value={200 - (daysLeftInSeason / 97) * 100}
              classes={{
                root: classes.seasonRoot,
                bar: classes.seasonBar,
              }}
            />
          </Box>
          <Box minWidth={60}>
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ lineHeight: "2.5em", verticalAlign: "middle" }}
            >{`${Math.round(daysLeftInSeason)} days`}</Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" mb={2}>
          <Box width="100%" mr={1}>
            <LinearProgress
              variant="determinate"
              value={
                (currentSeasonTreasPacks / currSeasonReqTreasurePacks) * 100
              }
              classes={{
                root: classes.tpRoot,
                bar: classes.tpBar,
              }}
            />
          </Box>
          <Box minWidth={60}>
            <Typography
              variant="caption"
              color="textSecondary"
              style={{ lineHeight: "2.5em", verticalAlign: "middle" }}
            >{`${
              currentSeasonTreasPacks || "0"
            }/${currSeasonReqTreasurePacks} TP`}</Typography>
          </Box>
        </Box>

        {tpStatusReport({ missingPacks, statusDaysRemaining, classes })}
      </div>
    </>
  );
};
